import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePlaidLink } from "react-plaid-link";
import { toast } from "sonner";
import { Bank } from "@phosphor-icons/react";
import { useApi, money, errText } from "@/lib/api";
import { IntegrationCard, OwnerActions, statusLabel } from "@/components/integrations/IntegrationCard";
import { HealthNote, useHealth } from "@/components/integrations/Health";

const PlaidButton = ({ token, onSuccess, onExit }) => {
  const { open, ready, error } = usePlaidLink({ token, onSuccess, onExit });
  useEffect(() => { if (ready) open(); }, [ready, open]);
  useEffect(() => { if (error) onExit(error); }, [error]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

export const BankCard = ({ accounts }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["plaid"], queryFn: () => api.get("/plaid/status") });

  const start = async () => {
    setBusy(true);
    try { setToken((await api.post("/plaid/link-token")).link_token); }
    catch (e) { toast.error(errText(e, "Couldn't start bank connection")); setBusy(false); }
  };
  const onSuccess = async (public_token) => {
    try {
      await api.post("/plaid/exchange", { public_token });
      toast.success("Bank connected — cash balances are now live");
      qc.invalidateQueries();
    } catch (e) { toast.error(errText(e, "Bank connection failed")); }
    setToken(null); setBusy(false);
  };
  const onExit = (err) => {
    setToken(null); setBusy(false);
    if (err) toast.error(err.display_message || "Bank connection didn't finish. Try again.");
  };
  const disconnect = async () => { await api.post("/plaid/disconnect"); qc.invalidateQueries(); toast("Bank disconnected"); };
  const h = useHealth()?.providers?.bank;
  const broken = data?.connected && h?.status === "needs_reconnect";

  return (
    <IntegrationCard testid="integration-bank" icon={Bank} color="#00FF9D" name="Bank accounts" status={statusLabel(data?.connected, h)}
      desc={data?.connected ? "Live balances from your connected bank accounts." : "Connect your business bank securely to pull real cash balances."}>
      <div className="space-y-2 mb-4">
        {accounts?.map((a) => (
          <div key={a.name + a.mask} className="flex justify-between text-sm font-mono"><span className="text-[#A3A3A3] truncate mr-2">{a.name} ··{a.mask}</span>{money(a.balance)}</div>
        ))}
      </div>
      {data?.connected && <HealthNote health={h} />}
      {token && <PlaidButton token={token} onSuccess={onSuccess} onExit={onExit} />}
      <OwnerActions testid="integration-bank">
      {data?.connected && !broken ? (
        <button data-testid="bank-disconnect-btn" onClick={disconnect} className="w-full rounded-full border border-[#2A2A2A] py-2.5 text-sm hover:border-[#FF5C7A] hover:text-[#FF5C7A] transition-colors">Disconnect</button>
      ) : (
        <>
          <button data-testid="bank-connect-btn" onClick={start} disabled={busy || (data && !data.configured)}
            className="w-full rounded-full bg-[#00FF9D] py-2.5 text-sm font-semibold text-[#050505] hover:shadow-[0_0_20px_rgba(0,255,157,0.5)] transition-shadow disabled:bg-[#2A2A2A] disabled:text-[#8A8A8A] disabled:cursor-not-allowed">
            {busy ? "Opening…" : broken ? "Reconnect bank" : "Connect bank"}
          </button>
          {data && !data.configured && <p data-testid="bank-connect-pending" className="mt-2 text-[11px] text-[#A3A3A3]">Bank feed keys not added yet — using sample balances.</p>}
        </>
      )}
      </OwnerActions>
    </IntegrationCard>
  );
};
